import { Button, Modal, Tag, Typography } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsLtaReport } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'

import {
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from './ffsLtaPublishApi'
import { getSnapshotSections } from './FfsReportSnapshotSections'
import { FfsResultType } from './ffsTypes'

const { Text, Title } = Typography

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const RowLabel = styled(Text)`
  width: ${LABEL_WIDTH};
`

const TextRow: FC<{ label: ReactNode; text: string | null }> = ({
  label,
  text,
}) => (
  <Row>
    <RowLabel>{label}</RowLabel>
    <Text>{text ?? '—'}</Text>
  </Row>
)

const ResultRow: FC<{ label: ReactNode; result: FfsResultType }> = ({
  label,
  result,
}) => (
  <Row>
    <RowLabel>{label}</RowLabel>
    <Tag color={RESULT_OUTCOME_COLORS[result]}>
      {RESULT_OUTCOME_LABELS[result]}
    </Tag>
  </Row>
)

// NOTE: The persisted `FfsLtaReport` (and the "report" nomenclature throughout
// this folder) is really a saved FFS *evaluation* snapshot, not a generated
// PDF/closeout report. The user-facing label is "FFS Evaluation"; the internal
// "report" naming is a legacy misnomer kept to avoid a repo-wide rename.
export interface FfsReportSnapshotModalProps {
  report: FfsLtaReport | null
  open: boolean
  onClose: () => void
  /** When set, renders a "Load into calculator" footer button. */
  onLoadIntoCalculator?: (report: FfsLtaReport) => void
}

export const FfsReportSnapshotModal: FC<FfsReportSnapshotModalProps> = ({
  report,
  open,
  onClose,
  onLoadIntoCalculator,
}) => {
  if (!report) return null

  const handleLoadIntoCalculator = () => {
    onLoadIntoCalculator?.(report)
    onClose()
  }

  return (
    <Modal
      title='FFS Evaluation'
      open={open}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key='close' onClick={onClose}>
          Close
        </Button>,
        ...(onLoadIntoCalculator
          ? [
              <Button
                key='load'
                type='primary'
                onClick={handleLoadIntoCalculator}>
                Load into calculator
              </Button>,
            ]
          : []),
      ]}>
      {getSnapshotSections(report).map((section) => (
        <div key={section.title}>
          <Title level={5}>{section.title}</Title>
          <SectionContainer>
            {section.rows.map((row) => {
              if (row.kind === 'measurement') {
                return (
                  <FfsParameterField
                    key={row.key}
                    label={row.label}
                    value={row.value}
                    measurementType={row.measurementType}
                    trailingText={row.trailingText}
                  />
                )
              }
              return row.key === 'result' ? (
                <ResultRow
                  key={row.key}
                  label={row.label}
                  result={report.outcome.result}
                />
              ) : (
                <TextRow key={row.key} label={row.label} text={row.text} />
              )
            })}
          </SectionContainer>
        </div>
      ))}
    </Modal>
  )
}
